import React, { useEffect, useState, useCallback, useRef } from 'react'
import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  onSnapshot, orderBy, query, serverTimestamp, Timestamp,
  getDocs,
} from 'firebase/firestore'
import { db } from '../../firebase/firebase'
import AdminLayout from './AdminLayout'
import { extractVideoId } from '../../utils/archiveUtils'

// ── Constants ──────────────────────────────────────────────────────────────────

const CATEGORIES = [
  { value: 'sermons',     label: 'Проповіді' },
  { value: 'bible_study', label: 'Вивчення Біблії' },
  { value: 'events',      label: 'Події' },
  { value: 'choir',       label: 'Хор' },
  { value: 'streams',     label: 'Трансляції' },
]

const CATEGORY_META = {
  sermons:     { label: 'Проповіді',        color: '#c0622a' },
  bible_study: { label: 'Вивчення Біблії',  color: '#b58a00' },
  events:      { label: 'Події',             color: '#3a8fd9' },
  choir:       { label: 'Хор',              color: '#7c5cbf' },
  streams:     { label: 'Трансляції',        color: '#3aaa6e' },
}

const categoryLabel = (v) => (CATEGORY_META[v] || {}).label || v
const categoryColor = (v) => (CATEGORY_META[v] || {}).color || '#c4986a'

// Old-to-new category mapping (for migrated data that may still have old values)
const LEGACY_CATEGORY_MAP = {
  sermon: 'sermons',
  word:   'bible_study',
  online: 'streams',
}
const normalizeCategory = (v) => LEGACY_CATEGORY_MAP[v] || v || 'sermons'

const ARCHIVE_PAGE_SIZE = 30

const emptyVideoForm = {
  titleUk:     '',
  titleEn:     '',
  category:    'sermons',
  speakerIds:  [],
  speakerNote: '',
  bible:       '',
  keywords:    '',
  series:      '',
  date:        '',
  link:        '',
}

const emptyPendingForm = {
  titleUk:     '',
  titleEn:     '',
  category:    'sermons',
  speakerIds:  [],
  speakerNote: '',
  bible:       '',
  keywords:    '',
  series:      '',
  date:        '',
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function dateToString(ts) {
  if (!ts) return ''
  const d = ts.toDate ? ts.toDate() : new Date(ts)
  return d.toISOString().slice(0, 10)
}

function detectCategoryFromTitle(title) {
  if (!title) return 'sermons'
  const t = title.toLowerCase()
  if (/^(хор|хоровий)/.test(t)) return 'choir'
  if (/^вивчення\s+слова?/.test(t)) return 'bible_study'
  if (/(богослуж|вечеря|свято|пряма\s*трансляц|онлайн\s*трансляц)/.test(t)) return 'streams'
  return 'sermons'
}

function buildArchiveDoc(form, pendingId = '') {
  const videoId = extractVideoId(form.link)
  const thumbnail = videoId
    ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
    : ''
  const year = form.date ? new Date(form.date).getFullYear() : new Date().getFullYear()
  const keywords = form.keywords
    .split(',')
    .map(k => k.trim().toLowerCase())
    .filter(Boolean)
  const category = normalizeCategory(form.category)

  return {
    videoId,
    link:       form.link.trim(),
    thumbnail,
    category,
    titleUk:    form.titleUk.trim(),
    titleEn:    form.titleEn.trim(),
    bible:      form.bible.trim(),
    keywords,
    series:     form.series.trim(),
    speakerIds:  form.speakerIds,
    speakerNote: form.speakerNote?.trim() || '',
    date:       form.date ? Timestamp.fromDate(new Date(form.date)) : Timestamp.fromDate(new Date()),
    year,
    createdAt:  serverTimestamp(),
    pendingId,
  }
}

// ── SpeakerMultiSelect ─────────────────────────────────────────────────────────

function SpeakerMultiSelect({ people, selectedIds, onChange }) {
  const sorted = [...people].sort((a, b) => a.nameUk.localeCompare(b.nameUk, 'uk'))
  return (
    <div style={s.speakerList}>
      {sorted.length === 0 && (
        <div style={{ fontSize: '12px', color: '#b8a090', padding: '4px 0' }}>
          Людей ще немає. Додайте проповідників нижче.
        </div>
      )}
      {sorted.map(p => {
        const checked = selectedIds.includes(p.id)
        return (
          <label key={p.id} style={s.speakerItem}>
            <input
              type='checkbox'
              checked={checked}
              onChange={() => {
                if (checked) {
                  onChange(selectedIds.filter(id => id !== p.id))
                } else {
                  onChange([...selectedIds, p.id])
                }
              }}
              style={{ marginRight: '6px', accentColor: '#8b6044' }}
            />
            <span style={{ fontSize: '13px', color: '#2d1f14' }}>{p.nameUk}</span>
            {p.nameEn && <span style={{ fontSize: '11px', color: '#9a7c63', marginLeft: '6px' }}>{p.nameEn}</span>}
            {p.role && <span style={s.roleChip}>{p.role}</span>}
          </label>
        )
      })}
    </div>
  )
}

// ── VideoAdminForm ─────────────────────────────────────────────────────────────

function VideoAdminForm({ form, onChange, onSave, onCancel, people, saving, mode, showLinkField }) {
  const [thumbError, setThumbError] = useState(false)
  const cat = form.category || 'sermons'
  const speakerRequired = cat === 'sermons' || cat === 'bible_study'
  const showBible  = cat !== 'choir'
  const showSeries = cat !== 'choir' && cat !== 'streams'
  const videoId = extractVideoId(form.link || '')
  const thumbSrc = videoId
    ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
    : null

  return (
    <div style={s.form}>
      <div style={s.formGrid}>

        {/* Link (only on manual add or pending approve if link is editable) */}
        {showLinkField && (
          <div style={{ ...s.field, gridColumn: '1 / -1' }}>
            <label style={s.label}>Посилання на YouTube *</label>
            <input
              value={form.link || ''}
              onChange={e => onChange('link', e.target.value)}
              style={s.input}
              placeholder='https://www.youtube.com/watch?v=...'
            />
            {thumbSrc && !thumbError && (
              <img
                src={thumbSrc}
                alt='thumbnail preview'
                onError={() => setThumbError(true)}
                style={{ marginTop: '8px', width: '160px', height: '90px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #f0e4d4' }}
              />
            )}
          </div>
        )}

        <div style={s.field}>
          <label style={s.label}>Назва (українська) *</label>
          <input
            value={form.titleUk}
            onChange={e => onChange('titleUk', e.target.value)}
            style={s.input}
            placeholder='Назва відео'
          />
        </div>

        <div style={s.field}>
          <label style={s.label}>Назва (англійська)</label>
          <input
            value={form.titleEn}
            onChange={e => onChange('titleEn', e.target.value)}
            style={s.input}
            placeholder='Video title in English'
          />
        </div>

        <div style={s.field}>
          <label style={s.label}>Категорія *</label>
          <select
            value={form.category}
            onChange={e => onChange('category', e.target.value)}
            style={s.input}
          >
            {CATEGORIES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>

        <div style={s.field}>
          <label style={s.label}>Дата відео *</label>
          <input
            type='date'
            value={form.date}
            onChange={e => onChange('date', e.target.value)}
            style={s.input}
            max={new Date().toISOString().slice(0, 10)}
          />
        </div>

        {showBible && (
          <div style={s.field}>
            <label style={s.label}>Біблійний текст</label>
            <input
              value={form.bible}
              onChange={e => onChange('bible', e.target.value)}
              style={s.input}
              placeholder='напр. Іс. 40:31'
            />
          </div>
        )}

        {showSeries && (
          <div style={s.field}>
            <label style={s.label}>Серія проповідей</label>
            <input
              value={form.series}
              onChange={e => onChange('series', e.target.value)}
              style={s.input}
              placeholder='назва серії або залиште порожнім'
            />
          </div>
        )}

        <div style={{ ...s.field, gridColumn: '1 / -1' }}>
          <label style={s.label}>Теги (через кому)</label>
          <input
            value={form.keywords}
            onChange={e => onChange('keywords', e.target.value)}
            style={s.input}
            placeholder='віра, надія, молитва'
          />
        </div>

        <div style={{ ...s.field, gridColumn: '1 / -1' }}>
          <label style={s.label}>
            Проповідник(и){speakerRequired ? ' *' : ' (необов\'язково)'}
          </label>
          <SpeakerMultiSelect
            people={people}
            selectedIds={form.speakerIds}
            onChange={ids => onChange('speakerIds', ids)}
          />
          <input
            value={form.speakerNote || ''}
            onChange={e => onChange('speakerNote', e.target.value)}
            style={{ ...s.input, marginTop: '8px' }}
            placeholder="або введіть ім'я вручну"
          />
        </div>

      </div>

      <div style={s.btnRow}>
        <button onClick={onSave} disabled={saving} style={s.btnPrimary}>
          {saving
            ? 'Зберігаємо…'
            : mode === 'add' ? '✓ Додати в архів' : '✓ Зберегти'}
        </button>
        <button onClick={onCancel} style={s.btnGhost}>Скасувати</button>
      </div>
    </div>
  )
}

// ── PeopleManager ──────────────────────────────────────────────────────────────

function PeopleManager({ people, onClose }) {
  const [addForm, setAddForm] = useState({ nameUk: '', nameEn: '', role: '', active: true })
  const [editId, setEditId] = useState(null)
  const [editForm, setEditForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(null)

  const handleAdd = async () => {
    if (!addForm.nameUk.trim()) { alert("Вкажіть ім'я (UA)"); return }
    setSaving(true)
    try {
      await addDoc(collection(db, 'archivePeople'), {
        nameUk:    addForm.nameUk.trim(),
        nameEn:    addForm.nameEn.trim(),
        role:      addForm.role.trim(),
        active:    addForm.active,
        createdAt: serverTimestamp(),
      })
      setAddForm({ nameUk: '', nameEn: '', role: '', active: true })
    } catch (err) {
      alert('Помилка: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleEditStart = (p) => {
    setEditId(p.id)
    setEditForm({ nameUk: p.nameUk, nameEn: p.nameEn || '', role: p.role || '', active: p.active !== false })
  }

  const handleEditSave = async () => {
    if (!editForm.nameUk.trim()) { alert("Вкажіть ім'я (UA)"); return }
    setSaving(true)
    try {
      await updateDoc(doc(db, 'archivePeople', editId), {
        nameUk: editForm.nameUk.trim(),
        nameEn: editForm.nameEn.trim(),
        role:   editForm.role.trim(),
        active: editForm.active,
      })
      setEditId(null)
    } catch (err) {
      alert('Помилка: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (p) => {
    if (!window.confirm(`Видалити "${p.nameUk}"? Посилання у відео залишаться (але ім'я не відображатиметься).`)) return
    setDeleting(p.id)
    try {
      await deleteDoc(doc(db, 'archivePeople', p.id))
    } catch (err) {
      alert('Помилка: ' + err.message)
    } finally {
      setDeleting(null)
    }
  }

  const sorted = [...people].sort((a, b) => a.nameUk.localeCompare(b.nameUk, 'uk'))

  return (
    <div style={s.card}>
      <div style={s.cardHeader}>
        <span style={s.cardIcon}>👤</span>
        <span style={s.cardTitle}>Проповідники / Люди</span>
        <button onClick={onClose} style={{ ...s.btnGhost, marginLeft: 'auto', padding: '4px 12px' }}>✕ Закрити</button>
      </div>

      {/* Add new person */}
      <div style={{ marginBottom: '20px', padding: '14px', background: '#fdfaf7', borderRadius: '10px', border: '1px solid #f0e4d4' }}>
        <div style={{ fontSize: '12px', fontWeight: '700', color: '#8b6044', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>Додати людину</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '8px', alignItems: 'end' }}>
          <div>
            <label style={s.label}>Ім'я (UA) *</label>
            <input value={addForm.nameUk} onChange={e => setAddForm(f => ({ ...f, nameUk: e.target.value }))}
              style={s.input} placeholder='Іван Коваленко' />
          </div>
          <div>
            <label style={s.label}>Ім'я (EN)</label>
            <input value={addForm.nameEn} onChange={e => setAddForm(f => ({ ...f, nameEn: e.target.value }))}
              style={s.input} placeholder='Ivan Kovalenko' />
          </div>
          <div>
            <label style={s.label}>Роль</label>
            <input value={addForm.role} onChange={e => setAddForm(f => ({ ...f, role: e.target.value }))}
              style={s.input} placeholder='pastor / deacon / guest' />
          </div>
          <button onClick={handleAdd} disabled={saving} style={{ ...s.btnPrimary, alignSelf: 'end', whiteSpace: 'nowrap' }}>
            + Додати
          </button>
        </div>
      </div>

      {/* People list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {sorted.map(p => (
          <div key={p.id} style={{ border: '1px solid #f0e4d4', borderRadius: '8px', background: '#fdfaf7', overflow: 'hidden' }}>
            {editId === p.id ? (
              <div style={{ padding: '10px 12px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto auto', gap: '8px', alignItems: 'center' }}>
                <input value={editForm.nameUk} onChange={e => setEditForm(f => ({ ...f, nameUk: e.target.value }))}
                  style={s.input} placeholder="Ім'я (UA)" />
                <input value={editForm.nameEn} onChange={e => setEditForm(f => ({ ...f, nameEn: e.target.value }))}
                  style={s.input} placeholder='Name (EN)' />
                <input value={editForm.role} onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))}
                  style={s.input} placeholder='Роль' />
                <button onClick={handleEditSave} disabled={saving} style={s.btnSm}>✓</button>
                <button onClick={() => setEditId(null)} style={{ ...s.btnSm, background: '#f5ede3', color: '#8b6044' }}>✕</button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px' }}>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#2d1f14', flex: 1 }}>{p.nameUk}</span>
                {p.nameEn && <span style={{ fontSize: '12px', color: '#9a7c63' }}>{p.nameEn}</span>}
                {p.role && <span style={s.roleChip}>{p.role}</span>}
                {p.active === false && <span style={{ ...s.roleChip, background: '#f0e4d4', color: '#9a7c63' }}>архів</span>}
                <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
                  <button onClick={() => handleEditStart(p)} style={s.iconBtn}>✏️</button>
                  <button onClick={() => handleDelete(p)} disabled={deleting === p.id}
                    style={{ ...s.iconBtn, color: '#b84040' }}>{deleting === p.id ? '…' : '🗑'}</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {sorted.length === 0 && <div style={s.empty}>Ще немає людей. Додайте першого проповідника вище.</div>}
      </div>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

const AdminArchive = () => {
  // people
  const [people, setPeople]           = useState([])
  const [showPeople, setShowPeople]   = useState(false)

  // pending
  const [pending, setPending]         = useState([])
  const [expandedPending, setExpandedPending] = useState(null)
  const [pendingForms, setPendingForms]       = useState({})
  const [approving, setApproving]     = useState(null)
  const [skipping, setSkipping]       = useState(null)

  // archive
  const [archive, setArchive]         = useState([])
  const [archiveLoading, setArchiveLoading] = useState(true)
  const [archiveSearch, setArchiveSearch]   = useState('')
  const [archiveCatFilter, setArchiveCatFilter] = useState('')
  const [archivePage, setArchivePage] = useState(1)
  const [expandedArchive, setExpandedArchive] = useState(null)
  const [editForms, setEditForms]     = useState({})
  const [saving, setSaving]           = useState(null)
  const [deleting, setDeleting]       = useState(null)

  // add-new form
  const [showAddForm, setShowAddForm] = useState(false)
  const [addForm, setAddForm]         = useState(emptyVideoForm)
  const [addSaving, setAddSaving]     = useState(false)

  // ── People listener ──────────────────────────────────────────────────────────

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'archivePeople'), orderBy('nameUk')),
      snap => setPeople(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    )
    return unsub
  }, [])

  // ── Pending listener ─────────────────────────────────────────────────────────

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'pendingVideos'), orderBy('publishedAt', 'desc')),
      snap => {
        setPending(
          snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(v => !v.skipped)
        )
      }
    )
    return unsub
  }, [])

  // ── Archive load ─────────────────────────────────────────────────────────────

  const loadArchive = useCallback(() => {
    setArchiveLoading(true)
    getDocs(query(collection(db, 'archive'), orderBy('year', 'desc')))
      .then(snap => {
        setArchive(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        setArchiveLoading(false)
      })
      .catch(() => setArchiveLoading(false))
  }, [])

  useEffect(() => { loadArchive() }, [loadArchive])

  // ── Pending helpers ──────────────────────────────────────────────────────────

  const handlePendingExpand = (id, video) => {
    if (expandedPending === id) { setExpandedPending(null); return }
    setExpandedPending(id)
    if (!pendingForms[id]) {
      const date = video.publishedAt
        ? new Date(video.publishedAt).toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10)
      setPendingForms(f => ({
        ...f,
        [id]: {
          ...emptyPendingForm,
          titleUk:  video.title || '',
          date,
          category: detectCategoryFromTitle(video.title),
        },
      }))
    }
  }

  const setPendingField = (id, field, val) =>
    setPendingForms(f => ({ ...f, [id]: { ...f[id], [field]: val } }))

  const handleApprove = async (video) => {
    const form = pendingForms[video.id]
    if (!form || !form.titleUk.trim()) { alert('Вкажіть назву (UA)'); return }
    if (!form.date) { alert('Вкажіть дату'); return }
    setApproving(video.id)
    try {
      const docData = buildArchiveDoc(
        { ...form, link: video.link || '' },
        video.id
      )
      await addDoc(collection(db, 'archive'), {
        ...docData,
        thumbnail: video.thumbnail || docData.thumbnail,
      })
      await deleteDoc(doc(db, 'pendingVideos', video.id))
      setExpandedPending(null)
      loadArchive()
    } catch (err) {
      alert('Помилка: ' + err.message)
    } finally {
      setApproving(null)
    }
  }

  const handleSkip = async (id) => {
    if (!window.confirm("Пропустити це відео? Воно більше не з'являтиметься у черзі.")) return
    setSkipping(id)
    try {
      await updateDoc(doc(db, 'pendingVideos', id), { skipped: true })
    } catch (err) {
      alert('Помилка: ' + err.message)
    } finally {
      setSkipping(null)
    }
  }

  // ── Add new archive entry ────────────────────────────────────────────────────

  const handleAddSave = async () => {
    if (!addForm.titleUk.trim()) { alert('Вкажіть назву (UA)'); return }
    if (!addForm.date) { alert('Вкажіть дату'); return }
    if (!addForm.link.trim()) { alert('Вкажіть посилання на YouTube'); return }
    setAddSaving(true)
    try {
      await addDoc(collection(db, 'archive'), buildArchiveDoc(addForm, ''))
      setAddForm(emptyVideoForm)
      setShowAddForm(false)
      loadArchive()
    } catch (err) {
      alert('Помилка: ' + err.message)
    } finally {
      setAddSaving(false)
    }
  }

  // ── Archive edit ─────────────────────────────────────────────────────────────

  const handleArchiveExpand = (item) => {
    if (expandedArchive === item.id) { setExpandedArchive(null); return }
    setExpandedArchive(item.id)
    if (!editForms[item.id]) {
      setEditForms(f => ({
        ...f,
        [item.id]: {
          titleUk:     item.titleUk || item.title || '',
          titleEn:     item.titleEn || '',
          category:    normalizeCategory(item.category || item.type),
          speakerIds:  Array.isArray(item.speakerIds) ? item.speakerIds : [],
          speakerNote: item.speakerNote || item.author || '',
          bible:       item.bible || '',
          keywords:    Array.isArray(item.keywords)
            ? item.keywords.join(', ')
            : (typeof item.keywords === 'string' ? item.keywords : ''),
          series:      item.series || '',
          date:        dateToString(item.date) || (item.year ? `${item.year}-01-01` : ''),
          link:        item.link || '',
        },
      }))
    }
  }

  const setEditField = (id, field, val) =>
    setEditForms(f => ({ ...f, [id]: { ...f[id], [field]: val } }))

  const handleArchiveSave = async (id) => {
    const form = editForms[id]
    if (!form.titleUk.trim()) { alert('Назва (UA) обов\'язкова'); return }
    if (!form.date) { alert('Вкажіть дату'); return }
    setSaving(id)
    try {
      const videoId = extractVideoId(form.link)
      const year = form.date ? new Date(form.date).getFullYear() : new Date().getFullYear()
      const keywords = form.keywords
        .split(',')
        .map(k => k.trim().toLowerCase())
        .filter(Boolean)
      await updateDoc(doc(db, 'archive', id), {
        titleUk:     form.titleUk.trim(),
        titleEn:     form.titleEn.trim(),
        category:    normalizeCategory(form.category),
        speakerIds:  form.speakerIds,
        speakerNote: form.speakerNote?.trim() || '',
        bible:       form.bible.trim(),
        keywords,
        series:      form.series.trim(),
        date:       form.date ? Timestamp.fromDate(new Date(form.date)) : Timestamp.fromDate(new Date()),
        year,
        link:       form.link.trim(),
        videoId:    videoId || undefined,
        thumbnail:  videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : undefined,
      })
      setExpandedArchive(null)
      loadArchive()
    } catch (err) {
      alert('Помилка: ' + err.message)
    } finally {
      setSaving(null)
    }
  }

  const handleArchiveDelete = async (id, title) => {
    if (!window.confirm(`Видалити "${title}" з архіву?`)) return
    setDeleting(id)
    try {
      await deleteDoc(doc(db, 'archive', id))
      loadArchive()
    } catch (err) {
      alert('Помилка: ' + err.message)
    } finally {
      setDeleting(null)
    }
  }

  // ── Filtering + pagination ───────────────────────────────────────────────────

  const term = archiveSearch.trim().toLowerCase()
  const filtered = archive.filter(v => {
    const cat = normalizeCategory(v.category || v.type)
    if (archiveCatFilter && cat !== archiveCatFilter) return false
    if (!term) return true
    const title = (v.titleUk || v.title || '').toLowerCase()
    const speakerMatch = (v.speakerIds || []).some(id => {
      const p = people.find(p => p.id === id)
      return p && p.nameUk.toLowerCase().includes(term)
    })
    return (
      title.includes(term) ||
      String(v.year).includes(term) ||
      categoryLabel(cat).toLowerCase().includes(term) ||
      speakerMatch
    )
  })

  useEffect(() => { setArchivePage(1) }, [archiveSearch, archiveCatFilter])

  const totalPages = Math.max(1, Math.ceil(filtered.length / ARCHIVE_PAGE_SIZE))
  const safePage   = Math.min(archivePage, totalPages)
  const pageItems  = filtered.slice((safePage - 1) * ARCHIVE_PAGE_SIZE, safePage * ARCHIVE_PAGE_SIZE)

  // ── Resolve speaker names ────────────────────────────────────────────────────

  const resolveSpeakers = (speakerIds) => {
    if (!Array.isArray(speakerIds) || speakerIds.length === 0) return ''
    return speakerIds
      .map(id => {
        const p = people.find(p => p.id === id)
        return p ? p.nameUk : null
      })
      .filter(Boolean)
      .join(', ')
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <AdminLayout>
      <div style={s.page}>

        <div style={s.pageHeader}>
          <div>
            <h1 style={s.pageTitle}>🎬 Архів відео</h1>
            <p style={s.pageDesc}>
              {archive.length} записів в архіві · {pending.length} в черзі · {people.length} людей
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button onClick={() => setShowPeople(p => !p)} style={s.btnGhost}>
              {showPeople ? '✕ Закрити людей' : '👤 Люди'}
            </button>
            <button onClick={() => { setShowAddForm(f => !f); setAddForm(emptyVideoForm) }} style={s.btnPrimary}>
              {showAddForm ? '✕ Скасувати' : '+ Додати відео'}
            </button>
          </div>
        </div>

        {/* ── People manager ── */}
        {showPeople && (
          <div style={{ marginBottom: '24px' }}>
            <PeopleManager people={people} onClose={() => setShowPeople(false)} />
          </div>
        )}

        {/* ── Manual add form ── */}
        {showAddForm && (
          <div style={{ ...s.card, marginBottom: '24px' }}>
            <div style={s.cardHeader}>
              <span style={s.cardIcon}>➕</span>
              <span style={s.cardTitle}>Нове відео (ручне додавання)</span>
            </div>
            <VideoAdminForm
              form={addForm}
              onChange={(field, val) => setAddForm(f => ({ ...f, [field]: val }))}
              onSave={handleAddSave}
              onCancel={() => { setShowAddForm(false); setAddForm(emptyVideoForm) }}
              people={people}
              saving={addSaving}
              mode='add'
              showLinkField={true}
            />
          </div>
        )}

        {/* ── Pending ── */}
        <div style={{ ...s.card, marginBottom: '24px' }}>
          <div style={s.cardHeader}>
            <span style={s.cardIcon}>⏳</span>
            <span style={s.cardTitle}>Нові відео з YouTube</span>
            <span style={s.badge}>{pending.length}</span>
          </div>

          {pending.length === 0 && (
            <div style={s.empty}>Черга порожня. Зачекайте на наступне відео або запустіть синхронізацію.</div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {pending.map(video => {
              const form = pendingForms[video.id] || emptyPendingForm
              const isOpen = expandedPending === video.id
              return (
                <div key={video.id} style={s.pendingRow}>
                  <div style={s.pendingSummary} onClick={() => handlePendingExpand(video.id, video)}>
                    {video.thumbnail && (
                      <img src={video.thumbnail} alt='' style={s.thumb} />
                    )}
                    <div style={s.pendingInfo}>
                      <div style={s.pendingTitle}>{video.title}</div>
                      <div style={s.pendingMeta}>{video.publishedAt ? video.publishedAt.slice(0, 10) : ''}</div>
                    </div>
                    <div style={s.pendingActions}>
                      <a
                        href={video.link}
                        target='_blank'
                        rel='noopener noreferrer'
                        onClick={e => e.stopPropagation()}
                        style={s.ytLink}
                      >
                        ▶ YouTube
                      </a>
                      <span style={{ ...s.expandBtn, transform: isOpen ? 'rotate(180deg)' : 'none' }}>▾</span>
                    </div>
                  </div>

                  {isOpen && (
                    <VideoAdminForm
                      form={form}
                      onChange={(field, val) => setPendingField(video.id, field, val)}
                      onSave={() => handleApprove(video)}
                      onCancel={() => setExpandedPending(null)}
                      people={people}
                      saving={approving === video.id}
                      mode='add'
                      showLinkField={false}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* ── Archive list ── */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <span style={s.cardIcon}>📁</span>
            <span style={s.cardTitle}>Записи в архіві</span>
            <span style={s.badge}>{filtered.length}</span>

            <select
              value={archiveCatFilter}
              onChange={e => setArchiveCatFilter(e.target.value)}
              style={{ ...s.input, maxWidth: '180px', marginLeft: 'auto' }}
            >
              <option value=''>Всі категорії</option>
              {CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>

            <input
              value={archiveSearch}
              onChange={e => setArchiveSearch(e.target.value)}
              placeholder='Пошук…'
              style={{ ...s.input, maxWidth: '200px' }}
            />

            <button onClick={loadArchive} style={s.btnGhost} title='Оновити'>↺</button>
          </div>

          {archiveLoading && <div style={s.empty}>Завантаження…</div>}

          {!archiveLoading && filtered.length === 0 && (
            <div style={s.empty}>Нічого не знайдено</div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {pageItems.map(item => {
              const isOpen = expandedArchive === item.id
              const form = editForms[item.id] || {}
              const cat = normalizeCategory(item.category || item.type)
              const speakerNames = resolveSpeakers(item.speakerIds)
              const displayTitle = item.titleUk || item.title || '(без назви)'
              const displayDate = dateToString(item.date)

              return (
                <div key={item.id} style={s.archiveRow}>
                  <div style={s.archiveSummary}>
                    {item.thumbnail && (
                      <img src={item.thumbnail} alt='' style={s.archiveThumb} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={s.archiveTitle}
                        onClick={() => handleArchiveExpand(item)}
                      >
                        {displayTitle}
                      </div>
                      <div style={s.archiveMeta}>
                        {displayDate && <span>{displayDate}</span>}
                        {speakerNames && <span>· {speakerNames}</span>}
                      </div>
                    </div>
                    <span
                      style={{
                        ...s.archiveCatPill,
                        background: categoryColor(cat),
                      }}
                    >
                      {categoryLabel(cat)}
                    </span>
                    <div style={s.archiveActions}>
                      <button onClick={() => handleArchiveExpand(item)} style={{ ...s.iconBtn, color: '#6b4a32' }}>✏️</button>
                      <button
                        onClick={() => handleArchiveDelete(item.id, displayTitle)}
                        disabled={deleting === item.id}
                        style={{ ...s.iconBtn, color: '#b84040' }}
                      >
                        {deleting === item.id ? '…' : '🗑'}
                      </button>
                    </div>
                  </div>

                  {isOpen && (
                    <VideoAdminForm
                      form={form}
                      onChange={(field, val) => setEditField(item.id, field, val)}
                      onSave={() => handleArchiveSave(item.id)}
                      onCancel={() => setExpandedArchive(null)}
                      people={people}
                      saving={saving === item.id}
                      mode='edit'
                      showLinkField={true}
                    />
                  )}
                </div>
              )
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={s.pagination}>
              <button
                disabled={safePage === 1}
                onClick={() => setArchivePage(safePage - 1)}
                style={{ ...s.pageBtn, opacity: safePage === 1 ? 0.4 : 1 }}
              >←</button>
              <span style={{ fontSize: '13px', color: '#7a5030' }}>{safePage} / {totalPages}</span>
              <button
                disabled={safePage === totalPages}
                onClick={() => setArchivePage(safePage + 1)}
                style={{ ...s.pageBtn, opacity: safePage === totalPages ? 0.4 : 1 }}
              >→</button>
            </div>
          )}
        </div>

      </div>
    </AdminLayout>
  )
}

export default AdminArchive

// ── Styles ────────────────────────────────────────────────────────────────────

const s = {
  page:       { maxWidth: '960px' },
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', gap: '12px', flexWrap: 'wrap' },
  pageTitle:  { margin: '0 0 6px', fontSize: '24px', fontWeight: '700', color: '#2d1f14' },
  pageDesc:   { margin: 0, fontSize: '14px', color: '#9a7c63' },

  card: {
    background: '#fff', borderRadius: '14px', padding: '28px',
    boxShadow: '0 2px 16px rgba(92,61,46,0.07)', border: '1px solid #f0e4d4',
  },
  cardHeader: {
    display: 'flex', alignItems: 'center', gap: '10px',
    marginBottom: '20px', paddingBottom: '14px', borderBottom: '1px solid #f5ede3',
    flexWrap: 'wrap',
  },
  cardIcon:  { fontSize: '18px' },
  cardTitle: { fontSize: '15px', fontWeight: '700', color: '#2d1f14' },
  badge: {
    background: '#f0e6d8', color: '#8b6044',
    padding: '2px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600',
  },
  empty: { color: '#b8a090', fontSize: '14px', padding: '12px 0', textAlign: 'center' },

  // pending
  pendingRow:     { border: '1px solid #f0e4d4', borderRadius: '10px', overflow: 'hidden', background: '#fdfaf7', marginBottom: '4px' },
  pendingSummary: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', cursor: 'pointer' },
  thumb:          { width: '80px', height: '45px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 },
  pendingInfo:    { flex: 1, minWidth: 0 },
  pendingTitle:   { fontSize: '14px', fontWeight: '600', color: '#2d1f14', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  pendingMeta:    { fontSize: '12px', color: '#9a7c63' },
  pendingActions: { display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 },
  ytLink:         { fontSize: '12px', color: '#c00', textDecoration: 'none', fontWeight: '600' },
  expandBtn:      { fontSize: '18px', color: '#9a7c63', display: 'inline-block', transition: 'transform 0.15s' },

  // archive list
  archiveRow:     { border: '1px solid #f0e4d4', borderRadius: '8px', overflow: 'hidden', background: '#fdfaf7', marginBottom: '4px' },
  archiveSummary: { display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px' },
  archiveThumb:   { width: '64px', height: '36px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 },
  archiveTitle:   { fontSize: '13px', fontWeight: '600', color: '#2d1f14', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  archiveMeta:    { fontSize: '11px', color: '#9a7c63', display: 'flex', gap: '6px', marginTop: '2px' },
  archiveCatPill: { fontSize: '11px', color: '#fff', padding: '2px 8px', borderRadius: '10px', flexShrink: 0, whiteSpace: 'nowrap' },
  archiveActions: { display: 'flex', gap: '4px', flexShrink: 0 },
  iconBtn:        { background: 'none', border: 'none', cursor: 'pointer', fontSize: '15px', padding: '2px 5px', borderRadius: '4px' },

  // form
  form:     { padding: '16px 14px 14px', borderTop: '1px solid #f0e4d4', background: '#fff' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' },
  field:    { marginBottom: '14px' },
  label:    { display: 'block', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase', color: '#8b6044', marginBottom: '6px' },
  input:    { width: '100%', padding: '9px 12px', border: '1.5px solid #e8d8c8', borderRadius: '7px', fontSize: '13px', color: '#2d1f14', background: '#fdfaf7', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' },

  // speaker select
  speakerList: { border: '1.5px solid #e8d8c8', borderRadius: '7px', background: '#fdfaf7', padding: '8px', maxHeight: '160px', overflowY: 'auto' },
  speakerItem: { display: 'flex', alignItems: 'center', padding: '4px 0', cursor: 'pointer', userSelect: 'none' },
  roleChip:    { fontSize: '10px', background: '#f0e6d8', color: '#8b6044', padding: '1px 6px', borderRadius: '8px', marginLeft: '6px' },

  btnRow:     { display: 'flex', gap: '10px', marginTop: '4px' },
  btnPrimary: { padding: '9px 20px', background: 'linear-gradient(135deg, #6b4a32, #8b6044)', color: '#fff', border: 'none', borderRadius: '7px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
  btnGhost:   { padding: '9px 16px', background: 'transparent', color: '#8b6044', border: '1.5px solid #e8d8c8', borderRadius: '7px', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' },
  btnSm:      { padding: '6px 12px', borderRadius: '7px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600', background: '#8b6044', color: '#fff', fontFamily: 'inherit', flexShrink: 0 },

  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '20px' },
  pageBtn:    { padding: '6px 14px', border: '1.5px solid #e8d8c8', background: '#fff', color: '#6b4a32', borderRadius: '7px', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' },
}
